import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useBetSlip } from "@/context/BetSlipContext";
import { usePoller } from "@/lib/usePoller";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { hasStartedPregame } from "@/lib/sportsbookPriority";

/* MobileSportsbook (design 3a) — the sports tab root.
   Sport strip (All / Football / Basketball / Baseball / Hockey / Soccer) → league
   chips (dynamic from the board + a Live toggle) → one card per game with
   Spread / Total / Money columns. Picks collect in the docked slip pill (shell).

   REAL data: GET /events (polled every 1s via usePoller for live price pulses).
   Suspended/closed outcomes render a lock (never a tappable fake price). Picks go
   through useBetSlip().toggle(leg) with the EXACT leg shape the desktop book uses
   (so the shared slip engine, price-acceptance and placement all stay intact). */

const SPORTS = [
  { id: "all", label: "All", icon: null },
  { id: "football", label: "Football", icon: "football" },
  { id: "basketball", label: "Basketball", icon: "basketball" },
  { id: "baseball", label: "Baseball", icon: "baseball" },
  { id: "hockey", label: "Hockey", icon: "hockey" },
  { id: "soccer", label: "Soccer", icon: "soccer" },
];

// Map an event to one of the strip tokens (mirrors the desktop sport filter intent).
function sportToken(e) {
  const s = `${e.sport || ""} ${e.league || ""}`.toLowerCase();
  if (/basket|nba|ncaab|wnba/.test(s)) return "basketball";
  if (/baseball|mlb/.test(s)) return "baseball";
  if (/hockey|nhl/.test(s)) return "hockey";
  if (/soccer|football association|mls|epl|uefa|la liga|serie a|bundesliga/.test(s)) return "soccer";
  if (/football|nfl|ncaaf|cfl/.test(s)) return "football";
  return "football";
}

const signed = (line) => {
  const n = Number(line);
  if (!Number.isFinite(n)) return line ?? "";
  return n > 0 ? `+${n}` : `${n}`;
};

function badgeInitials(name = "") {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  const last = w[w.length - 1];
  return last.slice(0, 3).toUpperCase();
}

export default function MobileSportsbook() {
  const navigate = useNavigate();
  const { toggle, has } = useBetSlip();
  const oddsFmt = useOddsFormat();
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sport, setSport] = useState("all");
  const [league, setLeague] = useState("ALL");
  const [liveOnly, setLiveOnly] = useState(false);

  // live-refresh the board every 1s (same cadence as the desktop book). On a stall we keep the
  // last-good board on screen; the server stays the sole authority on every placement. The poller's
  // own {failures,lastOkAt} drive the loading/error states — no separate fetch.
  const { failures, lastOkAt, refresh } = usePoller(async () => {
    const { data } = await api.get("/events");
    setEvents(Array.isArray(data) ? data : []);
    setLoaded(true);
  }, { interval: 1000, staleAfter: 6000, enabled: true });
  // Error only when the FIRST load never succeeded (never wipe a good board on a transient blip).
  const error = lastOkAt == null && failures > 0;

  const isChildProp = (e) => e.event_type === "prop" && e.parent_event_id;

  // matches that are real head-to-head games (not outright/prop containers)
  const matches = useMemo(
    () => events.filter((e) => !isChildProp(e) && e.event_type !== "outright" && e.home_team && e.away_team),
    [events],
  );

  const bySport = sport === "all" ? matches : matches.filter((e) => sportToken(e) === sport);

  // dynamic league chips from whatever is actually on the board for this sport
  const leagues = useMemo(() => {
    const set = [];
    bySport.forEach((e) => { const l = e.league || ""; if (l && !set.includes(l)) set.push(l); });
    return set;
  }, [bySport]);

  const shown = bySport
    .filter((e) => league === "ALL" || e.league === league)
    .filter((e) => !liveOnly || e.status === "live");

  // group by day header
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach((e) => {
      const key = e.status === "live"
        ? "Live now"
        : e.start_time
          ? new Date(e.start_time).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })
          : "Upcoming";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    });
    return [...m.entries()];
  }, [shown]);

  const pick = (event, outcome) => {
    const eventLabel = `${event.away_team} @ ${event.home_team}`;
    toggle({
      eventId: event.id, outcomeId: String(outcome.id), label: `${outcome.label}`, odds: outcome.odds,
      marketVersion: outcome.market_version ?? null, line: outcome.line ?? null, side: outcome.side ?? null,
      receivedAt: outcome.received_at ?? null, providerUpdatedAt: outcome.provider_updated_at ?? null,
      expiresAt: outcome.expires_at ?? null,
      eventLabel, market: outcome.market, league: event.league || null, sport: event.sport || null,
      live: event.status === "live",
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      {/* 1. sport strip */}
      <div className="m-no-scrollbar" style={{ display: "flex", gap: 10, padding: "0 12px", overflowX: "auto" }}>
        {SPORTS.map((s) => {
          const active = sport === s.id;
          return (
            <button key={s.id} type="button" onClick={() => { setSport(s.id); setLeague("ALL"); }}
              style={{ flex: "none", width: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "none", border: 0, cursor: "pointer" }}>
              <span style={{
                width: 48, height: 48, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center",
                background: active ? "#F4F4F5" : "linear-gradient(180deg,#1A1A1E 0%,#131316 100%)",
                boxShadow: active ? "0 6px 16px rgba(0,0,0,.4)" : "inset 0 0 0 1px rgba(255,255,255,.07),inset 0 1px 0 rgba(255,255,255,.06)",
              }}>
                {s.icon
                  ? <Icon name={s.icon} size={22} style={{ color: active ? "#0A0A0A" : "#a1a1aa" }} />
                  : <span style={{ font: "800 15px 'Manrope'", color: active ? "#0A0A0A" : "#a1a1aa" }}>All</span>}
              </span>
              <span style={{ font: "600 11px 'Outfit'", color: active ? "#fff" : "#71717a", whiteSpace: "nowrap" }}>{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* 2. league chips + Live toggle */}
      <div className="m-no-scrollbar" style={{ display: "flex", gap: 8, padding: "0 16px", overflowX: "auto", alignItems: "center" }}>
        <Chip label="All" active={league === "ALL"} onClick={() => setLeague("ALL")} />
        {leagues.map((l) => <Chip key={l} label={l} active={league === l} onClick={() => setLeague(l)} />)}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setLiveOnly((v) => !v)} aria-pressed={liveOnly}
          style={{ flex: "none", height: 34, borderRadius: 999, padding: "0 14px", display: "flex", alignItems: "center", gap: 7, border: 0, cursor: "pointer",
            background: liveOnly ? "rgba(239,68,68,.14)" : "#18181C", boxShadow: liveOnly ? "inset 0 0 0 1px rgba(239,68,68,.4)" : "inset 0 0 0 1px #26262B",
            color: liveOnly ? "#EF4444" : "#d4d4d8", font: "600 13px 'Outfit'" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#EF4444", animation: liveOnly ? "pnrPulse 1.6s ease-out infinite" : "none" }} />
          Live
        </button>
      </div>

      {/* 3. states */}
      {!loaded && <BoardSkeleton />}
      {error && shown.length === 0 && <Empty retry={refresh} />}
      {loaded && !error && shown.length === 0 && (
        <p style={{ textAlign: "center", color: "#71717a", font: "500 13px 'Outfit'", padding: "28px 16px" }}>
          {liveOnly ? "No live games right now." : "No games on the board right now."}
        </p>
      )}

      {/* 4. day groups + game cards */}
      {groups.map(([day, evs]) => (
        <div key={day} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) repeat(3,70px)", columnGap: 6, alignItems: "center", padding: "0 16px" }}>
            <span style={{ font: "800 15px 'Manrope'", letterSpacing: "-.01em" }}>{day}</span>
            {["SPREAD", "TOTAL", "MONEY"].map((h) => (
              <span key={h} style={{ textAlign: "center", font: "700 10px 'Outfit'", letterSpacing: ".1em", color: "#71717a" }}>{h}</span>
            ))}
          </div>
          {evs.map((e) => <GameCard key={e.id} event={e} oddsFmt={oddsFmt} onPick={pick} has={has} onOpen={() => navigate(`/event/${e.id}`)} />)}
        </div>
      ))}
    </div>
  );
}

function Chip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      style={{ flex: "none", height: 34, borderRadius: 999, padding: "0 14px", border: 0, cursor: "pointer",
        background: active ? "#F4F4F5" : "#18181C", boxShadow: active ? "none" : "inset 0 0 0 1px #26262B",
        color: active ? "#0A0A0A" : "#d4d4d8", font: active ? "700 13px 'Outfit'" : "600 13px 'Outfit'", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );
}

function GameCard({ event, oddsFmt, onPick, has, onOpen }) {
  const outs = event.outcomes || [];
  const cells = (side) => ({
    ml: outs.find((o) => o.market === "moneyline" && o.side === side),
    spread: outs.find((o) => o.market === "spread" && o.side === side),
    total: outs.find((o) => o.market === "total" && o.side === (side === "away" ? "over" : "under")),
  });
  const away = cells("away");
  const home = cells("home");
  const isLive = event.status === "live";
  const locked = !(event.status === "open" || event.status === "live") || hasStartedPregame(event);
  const liveScore = isLive && event.gs_home_score != null;

  const row = (side, name, c, score) => (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) repeat(3,70px)", columnGap: 6, alignItems: "center" }}>
      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
        <TeamBadge name={name} />
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 15px 'Outfit'", color: "#fff" }}>{name}</span>
        {score != null && <span className="m-tnum" style={{ flex: "none", marginLeft: "auto", font: "800 16px 'Manrope'", color: "#fff" }}>{score}</span>}
      </div>
      <OddsCell o={c.spread} line={c.spread ? signed(c.spread.line) : null} locked={locked} oddsFmt={oddsFmt} onPick={() => onPick(event, c.spread)} sel={c.spread && has(event.id, c.spread.id)} />
      <OddsCell o={c.total} line={c.total ? `${side === "away" ? "O" : "U"} ${c.total.line}` : null} locked={locked} oddsFmt={oddsFmt} onPick={() => onPick(event, c.total)} sel={c.total && has(event.id, c.total.id)} />
      <OddsCell o={c.ml} line={null} locked={locked} oddsFmt={oddsFmt} onPick={() => onPick(event, c.ml)} sel={c.ml && has(event.id, c.ml.id)} />
    </div>
  );

  return (
    <div style={{ margin: "0 16px", borderRadius: 18, padding: "12px 12px 14px", display: "flex", flexDirection: "column", gap: 8,
      background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 12px 28px rgba(0,0,0,.35)" }}>
      {/* meta row */}
      <button type="button" onClick={onOpen} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: 0, padding: 0, cursor: "pointer", width: "100%", textAlign: "left" }}>
        {isLive ? (
          <span style={{ display: "flex", alignItems: "center", gap: 5, height: 20, padding: "0 7px", borderRadius: 6, background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)", font: "800 10px 'Outfit'", letterSpacing: ".14em", color: "#EF4444" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF4444", animation: "pnrPulse 1.6s ease-out infinite" }} />
            {event.gs_period ? event.gs_period : "LIVE"}
          </span>
        ) : (
          <span style={{ display: "flex", alignItems: "center", gap: 6, font: "600 12px 'Outfit'", color: "#a1a1aa" }}>
            <Icon name="clock" size={12} style={{ color: "#71717a" }} />
            {event.start_time ? new Date(event.start_time).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" }) : "Upcoming"}
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, font: "600 12px 'Outfit'", color: "#71717a" }}>
          More bets <Icon name="chevron-right" size={13} style={{ color: "#52525b" }} />
        </span>
      </button>
      {row("away", event.away_team, away, liveScore ? event.gs_away_score : null)}
      {row("home", event.home_team, home, liveScore ? event.gs_home_score : null)}
    </div>
  );
}

function OddsCell({ o, line, locked, oddsFmt, onPick, sel }) {
  if (!o) {
    return <span style={{ height: 46, borderRadius: 12, boxShadow: "inset 0 0 0 1px #1A1A1E", display: "flex", alignItems: "center", justifyContent: "center", font: "700 14px 'Manrope'", color: "#3F3F46" }}>—</span>;
  }
  const isLock = locked || o.suspended === true || o.active === false;
  if (isLock) {
    return (
      <span title="Market suspended" style={{ height: 46, borderRadius: 12, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, cursor: "not-allowed" }}>
        {line && <span style={{ font: "600 11px 'Outfit'", color: "#52525b" }}>{line}</span>}
        <Icon name="lock" size={13} style={{ color: "#52525b" }} />
      </span>
    );
  }
  return (
    <button type="button" onClick={onPick} aria-pressed={!!sel}
      style={{ height: 46, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, border: 0, cursor: "pointer",
        background: sel ? "#00D632" : "#18181C",
        boxShadow: sel ? "inset 0 1px 0 rgba(255,255,255,.32)" : "inset 0 0 0 1px #26262B,inset 0 1px 0 rgba(255,255,255,.04)" }}>
      {line && <span style={{ font: "600 11px 'Outfit'", color: sel ? "rgba(10,10,10,.7)" : "#a1a1aa", whiteSpace: "nowrap" }}>{line}</span>}
      <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: sel ? "#0A0A0A" : "#fff", whiteSpace: "nowrap" }}>{formatOdds(o.odds, oddsFmt)}</span>
    </button>
  );
}

function TeamBadge({ name }) {
  return (
    <span style={{ width: 28, height: 28, flex: "none", borderRadius: "50%", background: "#1C1C21", boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", font: "800 9px 'Manrope'", color: "#E4E4E7" }}>
      {badgeInitials(name)}
    </span>
  );
}

function BoardSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ height: 118, borderRadius: 18, background: "#141417", boxShadow: "inset 0 0 0 1px #1F1F23" }} />
      ))}
    </div>
  );
}

function Empty({ retry }) {
  return (
    <div style={{ textAlign: "center", padding: "28px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      <p style={{ color: "#a1a1aa", font: "500 14px 'Outfit'" }}>We couldn’t load the board.</p>
      <button type="button" onClick={retry} style={{ height: 44, borderRadius: 14, padding: "0 18px", border: 0, cursor: "pointer", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", color: "#fff", font: "700 14px 'Outfit'" }}>Try again</button>
    </div>
  );
}
